import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Product } from '../data/products';
import './ProductCard.css';

interface ProductCardProps {
    product: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
    const { currentLanguage, isRTL } = useLanguage();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const title = currentLanguage.code === 'ar' ? product.titleAr : product.title;
    const description = currentLanguage.code === 'ar' ? product.descriptionAr : product.description;

    // Use images array or fallback to single image - wrapped in useMemo
    const productImages = useMemo(() => {
        return product.images && product.images.length > 0 ? product.images : [product.image];
    }, [product.images, product.image]);

    console.log(`Product ${product.id} images:`, productImages, 'current index:', currentImageIndex);

    // Preload images for smoother transitions
    useEffect(() => {
        if (productImages.length > 1) {
            productImages.forEach((imageSrc, index) => {
                if (index > 0) { // Skip first image as it's already loaded
                    const img = new Image();
                    img.src = imageSrc;
                    img.onload = () => console.log(`Preloaded image ${index}: ${imageSrc}`);
                    img.onerror = () => console.error(`Failed to load image ${index}: ${imageSrc}`);
                }
            });
        }
    }, [productImages]);

    // Simplified image cycling with smooth transitions
    useEffect(() => {
        if (isHovered && productImages.length > 1) {
            console.log(`Starting image cycling for product ${product.id}, images:`, productImages);

            intervalRef.current = setInterval(() => {
                setCurrentImageIndex((prevIndex) => {
                    const nextIndex = (prevIndex + 1) % productImages.length;
                    console.log(`Cycling image: ${prevIndex} -> ${nextIndex}, URL: ${productImages[nextIndex]}`);
                    return nextIndex;
                });
            }, 1500); // Change image every 1.5 seconds
        } else {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            setCurrentImageIndex(0);
            setIsTransitioning(false);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isHovered, productImages, product.id]);

    const handleMouseEnter = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    return (
        <div className={`product-card ${isRTL ? 'rtl' : 'ltr'}`}>
            <Link
                to={`/products/${product.category}/${product.id}`}
                state={{ product }}
                className="product-card-wrapper"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{ textDecoration: 'none', color: 'inherit' }}
            >
                <div className="product-image">
                    <img
                        src= { `https://api.dawaak-andna.com${productImages[currentImageIndex]}` }
                        alt={title}
                        className={`product-main-image ${isTransitioning ? 'transitioning' : ''}`}
                        key={currentImageIndex}
                    />
                    {productImages.length > 1 && (
                        <div className="image-indicator">
                            {productImages.map((_, index) => (
                                <span
                                    key={index}
                                    className={`indicator-dot ${index === currentImageIndex ? 'active' : ''}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
                <div className="product-info">
                    <h3 className="product-name">{title}</h3>
                    <p className="product-description">{description}</p>
                    <div className="product-footer">
                        <span className="product-category" style={
                            {
                                color: ' #d15f0eff'
                            }
                        }>{product.category}</span>
                        <div className="product-price">
                            <span className="price-amount">{product.price}</span>
                            <span className="price-currency">
                                {isRTL ? 'ريال سعودي' : 'SAR'}
                            </span>
                        </div>
                    </div>
                </div>
            </Link>
        </div>
    );
};

export default ProductCard;